import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AutopilotDevicesService } from '../../Services/Intune/autopilot-devices.service';
import { NextService } from '../../Services/Intune/next.service';
import {
    AutopilotDevicesIndexResponse,
    AutopilotImportHistoryResponse,
} from '../../Interfaces/api/Intune/autopilot-devices.interface';
import { AutopilotDevice } from '../../Models/Intune/autopilot-device.model';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../Modules/Shared/Services/auth.service';
import { PERMISSIONS } from '../../Enums/permission.enum';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';

@Injectable()
export class AutopilotDevicesFacade {
    private readonly _resourceState = inject(ResourceStateService);

    private readonly _autopilotDevicesService = inject(AutopilotDevicesService);
    private readonly _nextService = inject(NextService);
    private readonly _msgService = inject(MsgService);

    private readonly _isImporting = signal(false);
    private readonly _isLoadingMore = signal(false);
    private readonly _extraDevices = signal<AutopilotDevice[]>([]);
    private readonly _nextLink = signal<string | null>(null);

    private readonly _authService = inject(AuthService);
    readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });

    private readonly _baseResource = httpResource<AutopilotDevicesIndexResponse>(() => {
        if (!this.currentUser()?.hasPermission(PERMISSIONS.AUTOPILOT_DEVICES_READ)) {
            return undefined;
        }
        return this._autopilotDevicesService.index();
    });

    constructor() {
        this.syncFromBaseResource();
    }

    private syncFromBaseResource(): void {
        effect(() => {
            if (!this._baseResource.hasValue()) return;
            const value = this._resourceState.valueOf(this._baseResource);
            if (value?.data) {
                this._nextLink.set(value.data.next ?? null);
                this._extraDevices.set([]);
            }
        });
    }

    private readonly _importHistoryResource = httpResource<AutopilotImportHistoryResponse>(() => {
        if (!this.currentUser()?.hasPermission(PERMISSIONS.AUTOPILOT_DEVICES_IMPORT)) {
            return undefined;
        }
        return this._autopilotDevicesService.importHistoryRequest();
    });

    async import(file: File): Promise<void> {
        this._isImporting.set(true);

        try {
            await firstValueFrom(this._autopilotDevicesService.import(file));
            this._msgService.showSnackBar('app.autopilot_devices.import.success', { type: 'success' });
        } catch (error: any) {
            this._msgService.showSnackBar(error?.error?.message ?? 'app.autopilot_devices.import.error', { type: 'error' });
        } finally {
            this._isImporting.set(false);
        }
    }

    async delete(id: string | undefined): Promise<void> {
        if (!id) return;
        try {
            await firstValueFrom(this._autopilotDevicesService.delete(id));
            this._msgService.showSnackBar('app.autopilot_devices.delete.success', { type: 'success' });
        } catch (error: any) {
            this._msgService.showSnackBar(error?.error?.message ?? 'app.autopilot_devices.delete.error', { type: 'error' });
        }
    }

    reload(): void {
        this._baseResource.reload();
    }

    async loadMore(): Promise<void> {
        const next = this._nextLink();
        if (!next || this._isLoadingMore()) return;
        this._isLoadingMore.set(true);
        try {
            const response = await firstValueFrom(
                this._nextService.getNext<AutopilotDevice>(next)
            );
            const newDevices = response.data.value.map((d) => new AutopilotDevice(d));
            this._extraDevices.update((current) => [...current, ...newDevices]);
            this._nextLink.set(response.data.next ?? null);
        } finally {
            this._isLoadingMore.set(false);
        }
    }

    reloadImportHistory(): void {
        this._importHistoryResource.reload();
    }

    readonly devices = computed<AutopilotDevice[]>(() => {
        const base = this._baseResource.hasValue()
            ? (this._resourceState.valueOf(this._baseResource)?.data.value.map((d) => new AutopilotDevice(d)) ?? [])
            : [];
        return [...base, ...this._extraDevices()];
    });

    readonly hasMore = computed(() => !!this._nextLink());
    readonly isLoadingMore = computed(() => this._isLoadingMore());

    readonly importHistoryEntries = computed(() => {
        return this._resourceState.valueOf(this._importHistoryResource)?.data ?? [];
    });
    readonly importHistoryLoading = computed(() => this._importHistoryResource.isLoading());
    readonly importHistoryError = computed(() => this._importHistoryResource.error());

    readonly isLoading = computed(() => this._baseResource.isLoading());
    readonly isImporting = computed(() => this._isImporting());
    readonly error = computed(() => this._baseResource.error());
    readonly status = computed(() => this._baseResource.status());
}